import { ChangeDetectionStrategy, Component, Signal, WritableSignal, computed, inject, linkedSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { provideComponentStore } from '@ngrx/component-store';
import { IconComponent } from '@shared-lib/components/icon/icon.component';
import { ButtonDirective } from 'primeng/button';
import { map, pairwise } from 'rxjs/operators';
import { LoginStatusEnum } from './enums/login-status.enum';
import { LoginStatusStore } from './login-status.store';
import { LoginStatusDetailsModel } from './models/login-status-details.model';
import { LoginStatusSwapModel } from './models/login-status-swap.model';
import { LoginStatusDetailsRecord } from './records/login-status-details.record';

@Component({
  selector: 'cp-login-status',
  templateUrl: './login-status.component.html',
  styleUrls: ['../../auth-theme.scss', 'login-status.component.scss'],
  imports: [ButtonDirective, IconComponent],
  providers: [provideComponentStore(LoginStatusStore)],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginStatusComponent {
  private readonly store = inject(LoginStatusStore);

  protected readonly LoginStatusEnum = LoginStatusEnum;
  protected readonly LoginStatusDetailsRecord = LoginStatusDetailsRecord;

  protected readonly status: Signal<LoginStatusEnum> = toSignal(this.store.status$, { requireSync: true });

  protected readonly isEscapeLinkVisible: Signal<boolean> = toSignal(this.store.isEscapeLinkVisible$, { requireSync: true });

  protected readonly canSendNewSignInEmail: Signal<boolean> = toSignal(this.store.canSendNewSignInEmail$, { requireSync: true });

  protected readonly isSendingNewSignInEmail: Signal<boolean> = toSignal(this.store.isSendingNewSignInEmail$, { requireSync: true });

  protected readonly hasNewSignInEmailFailed: Signal<boolean> = toSignal(this.store.hasNewSignInEmailFailed$, { requireSync: true });

  protected readonly statusDetails: Signal<LoginStatusDetailsModel> = computed(() => LoginStatusDetailsRecord[this.status()]);

  protected readonly titleWords: Signal<string[]> = computed(() => this.statusDetails().title.split(' '));

  private readonly statusSwap: Signal<LoginStatusSwapModel | null> = toSignal(
    this.store.status$.pipe(
      pairwise(),
      map(([previousStatus], index): LoginStatusSwapModel => ({ previousStatus, index })),
    ),
    { initialValue: null },
  );

  protected readonly hasStatusSwapped: Signal<boolean> = computed(() => this.statusSwap() !== null);

  protected readonly outgoingStatusSwaps: WritableSignal<LoginStatusSwapModel[]> = linkedSignal(() => {
    const statusSwap = this.statusSwap();
    return statusSwap ? [statusSwap] : [];
  });

  protected onAnimationLoad(): void {
    this.store.setFirstLoopStartedAt(performance.now());
  }

  protected onOutgoingTextAnimationEnd(): void {
    this.outgoingStatusSwaps.set([]);
  }

  protected onTryAgain(): void {
    this.store.retrySignIn();
  }

  protected onSendNewSignInEmail(): void {
    this.store.sendNewSignInEmail();
  }

  protected onGoToLoginPage(): void {
    this.store.goToLoginPage();
  }
}
